import React, { useContext, useState, useEffect, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import classNames from 'classnames';
import { ReactComponent as EmptyCartImg } from '../../../media/images/empty-cart.svg';
import { OrderScreen } from '../OrderScreen';
import {
    OrderScreenContext,
    CheckoutFlowState,
} from 'features/order-summary/screens/CheckoutScreen';
import { OrderSummaryGrid } from '../components/summary/OrderSummaryGrid';
import { OrderSummaryOptions, showHeight } from '../components/summary/OrderSummaryOptions';

import { useRootSelector } from 'rootStore';
import { updateCart } from 'common/features/store/duck/cart/duck';
import { selectIsAuthenticated } from 'common/features/store/duck/account/duck';
import { removeItemFromCart } from 'common/api/e-comm/cart/lineitem';
import { Btn } from 'components/btn/Btn';
import { getPath, Routes } from 'routes';
import { EASE_IN_OUT_BACK } from 'styles/easing';
import { createStyles } from 'utils/createStyle';
import { gaLogBeginCheckout } from 'config/analytics';
import { FeatureFlag } from 'components/feature-flag';
import { CartInfoState } from '../CartInfoState';
import { selectIsShipToCoordinatorEnabled } from 'common/features/store/duck/organization/duck';
import { useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import { Value } from 'firebase/remote-config';
import { isInIframe, mainWebsiteOrigin } from 'utils';

const styles = createStyles({
    area: [
        'mb-2',
        {
            overflow: 'hidden',
            animationName: showHeight,
            animationDuration: '.5s',
            animationTimingFunction: EASE_IN_OUT_BACK,
            animationFillMode: 'forwards',
        },
    ],
});

export const OrderSummaryScreenContent = () => {
    const { cart, cartAsyncState } = useRootSelector((s) => s.store.cart);
    const { cartInfo, setCartInfo, checkoutFlowState, updatingLineItem } =
        useContext(OrderScreenContext);

    const { shipToHome } = cartInfo;
    const dispatch = useDispatch<any>();
    const history = useHistory();
    const [isClearingCart, setIsClearingCart] = useState(false);
    const disableCheckoutBtn = checkoutFlowState === CheckoutFlowState.DISABLED || updatingLineItem;
    const shipToCoordinatorDisable = !useSelector(selectIsShipToCoordinatorEnabled);
    const enableEditInCartQuantity = useFirebaseConfigValues<boolean>(
        'enable_edit_in_cart_quantity_web',
        true,
        (value: Value) => value.asBoolean()
    );
    const userIsLoggedIn = useRootSelector(selectIsAuthenticated);

    const clearCart = async () => {
        if (cart) {
            try {
                setIsClearingCart(true);

                const ids = cart.lineItems.map((li) => li.id);

                // Removing the items from cart serially to avoid issues with the backend when removing in parallel.
                for (let i = 0; i < ids.length; i++) {
                    await removeItemFromCart(ids[i]);
                }
                dispatch(updateCart({ cart: null }));
                window.parent.postMessage({ type: 'resetCart' }, mainWebsiteOrigin());
            } finally {
                setIsClearingCart(false);
            }
        }
    };

    const setShipToHome = useCallback(
        (value: boolean) => {
            setCartInfo((cartInfo: CartInfoState) => ({
                ...cartInfo,
                shipToHome: value,
            }));
        },
        [setCartInfo]
    );

    const goShopping = () => {
        if (isInIframe()) {
            window.parent.postMessage({ type: 'goShopping' }, mainWebsiteOrigin());
        } else {
            history.push(Routes.Brands);
        }
    };

    const beginCheckout = () => {
        const url = getPath(Routes.ShopOrderPayment, {
            shipToHome: shipToHome ? 'ship-to-home' : 'ship-to-coordinator',
        });
        if (cart) {
            if (isInIframe()) {
                window.parent.postMessage({ type: 'logBeginCheckout', cart }, mainWebsiteOrigin());
            } else {
                gaLogBeginCheckout(cart);
            }
        }
        history.push(url, cartInfo);
    };

    const isLoading = isClearingCart || cartAsyncState.loading;
    const shouldHideButtons = shipToCoordinatorDisable;

    /* Side Effect:
     * Sets the shipToHome boolean to `true` when shipToCoordinatorDisable is disabled.
     *
     * NOTE: The `shipToHome` variable controls how the cart line items gets rendered.
     *
     * Logic:
     * shipToCoordinatorDisable controls whether or not we can ship to coordinator.
     * if the value is `true` then the only other possible option is `ship to home`.
     */
    useEffect(() => {
        if (shipToCoordinatorDisable) {
            setShipToHome(shipToCoordinatorDisable);
        }
    }, [shipToCoordinatorDisable, setShipToHome]);

    const renderGrid = () => {
        return (
            <div className="w-full md:mr-4" data-testid="order-summary-grid">
                {!isLoading && cart && (
                    <>
                        <div className="mb-4 lg:mb-1 lg:block text-right mr-2 text-sm">
                            <button onClick={clearCart}>
                                <a href="#" className="text-grey-2 font-bold">
                                    Clear Cart
                                </a>
                            </button>
                        </div>
                        <OrderSummaryGrid
                            cart={cart}
                            loading={isLoading}
                            shipToHome={Boolean(shipToHome)}
                            onShipToHomeChange={setShipToHome}
                            hideButtons={shouldHideButtons}
                            hideEditQuantityButtons={!enableEditInCartQuantity}
                        />
                        <div className="block md:hidden">
                            <FeatureFlag
                                flagKey="enable_checkout_button"
                                fallback={true}
                                displayOnValue={true}
                            >
                                <Btn
                                    disabled={disableCheckoutBtn}
                                    className={'cart_begin_checkout_button w-full mt-10 lg:mt-4'}
                                    onClick={disableCheckoutBtn ? undefined : beginCheckout}
                                >
                                    Begin Checkout
                                </Btn>
                            </FeatureFlag>
                            <FeatureFlag flagKey="enable_checkout_button" displayOnValue={false}>
                                <Btn disabled={true} className={'w-full mt-10 lg:mt-4'}>
                                    Checkout Disabled
                                </Btn>
                            </FeatureFlag>
                        </div>
                    </>
                )}
                {isLoading && (
                    <>
                        <Skeleton height="9rem" className="mt-4" />
                        <Skeleton height="4rem" className="mt-4" />
                        <Skeleton height="4rem" className="mt-4" />
                        <Skeleton height="4rem" className="mt-4" />
                        <Skeleton height="4rem" className="mt-4" />
                    </>
                )}
            </div>
        );
    };

    const renderOrderOptions = () => {
        return (
            <div className="hidden md:block" style={{ width: '20%', minWidth: '16rem' }}>
                {cart && !cartAsyncState.loading && (
                    <OrderSummaryOptions shipToHome={Boolean(shipToHome)} hidePayment={true}>
                        <div className={classNames(styles.area, 'hidden md:block mt-5')}>
                            <FeatureFlag
                                flagKey="enable_checkout_button"
                                displayOnValue={true}
                                fallback={true}
                            >
                                <Btn
                                    disabled={disableCheckoutBtn}
                                    onClick={disableCheckoutBtn ? undefined : beginCheckout}
                                    className="cart_begin_checkout_button w-full mt-4"
                                >
                                    Begin Checkout
                                </Btn>
                            </FeatureFlag>
                            <FeatureFlag flagKey="enable_checkout_button" displayOnValue={false}>
                                <Btn disabled={true} className="w-full mt-4">
                                    Checkout Disabled
                                </Btn>
                            </FeatureFlag>
                        </div>
                    </OrderSummaryOptions>
                )}
                {cartAsyncState.loading && (
                    <>
                        <Skeleton height="10rem" />
                    </>
                )}
            </div>
        );
    };

    return (
        <>
            {isInIframe() && !userIsLoggedIn ? (
                <div>
                    <div className="text-center p-2">
                        <div className="text-3xl md:text-5xl text-brand-dark">
                            Your shopping cart is empty.
                        </div>
                        <div className="text-lg mt-4 mb-2 md:mb-12 text-grey-1">
                            Sign in to continue shopping.
                        </div>
                        <div className="flex justify-center items-center flex-col">
                            <EmptyCartImg
                                width="100%"
                                className="inline-block text-left mb-0 md:mb-12"
                                style={{ maxWidth: '1000px', width: '90%' }}
                            />
                            <Btn
                                className="w-full max-w-sm"
                                onClick={() =>
                                    window.parent.postMessage(
                                        { type: 'signIn' },
                                        mainWebsiteOrigin()
                                    )
                                }
                            >
                                Sign In
                            </Btn>
                        </div>
                    </div>
                </div>
            ) : (
                !cartAsyncState.loading &&
                (!cart || cart.lineItems.length <= 0) && (
                    <div>
                        <div className="text-center p-2">
                            <div className="text-3xl md:text-5xl text-brand-dark">
                                Your shopping cart is empty.
                            </div>
                            <div className="text-lg mt-4 mb-2 md:mb-12 text-grey-1">
                                Shop top brands and start earning now.
                            </div>
                            <EmptyCartImg
                                width="100%"
                                className="inline-block text-left mb-0 md:mb-12"
                                style={{ maxWidth: '1000px', width: '90%' }}
                            />
                            <Btn className="w-full max-w-sm" onClick={goShopping}>
                                Take Me Shopping
                            </Btn>
                        </div>
                    </div>
                )
            )}
            {(!cart || cart.lineItems.length > 0) && (
                <div className="w-full">
                    <div className="flex">
                        {renderGrid()}
                        {renderOrderOptions()}
                    </div>
                </div>
            )}
        </>
    );
};

export const OrderSummaryScreen = () => {
    const { cart, cartAsyncState } = useRootSelector((s) => s.store.cart);
    const className = isInIframe() ? 'pt-20' : 'pt-0';
    return (
        <OrderScreen
            title="Cart"
            orderStep="summary"
            className={className}
            showSummary={false}
            showMobileSummary={true}
            hideHeader={!cartAsyncState.loading && (!cart || cart.lineItems.length <= 0)}
            showBrandCardTiles={true}
            embedView={true}
        >
            <OrderSummaryScreenContent />
        </OrderScreen>
    );
};

export default OrderSummaryScreen;
